// src/pages/Orders/Orders.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Card, Alert, Row, Col, Form,
    Badge, Dropdown, Modal, InputGroup, Spinner
} from 'react-bootstrap';
import {
    FaEdit, FaTrash, FaSearch, FaEye, FaEllipsisV,
    FaPlus, FaShoppingCart, FaBox, FaInfoCircle, FaUser,
    FaStore, FaCalendar, FaReceipt, FaTruck, FaChevronRight,
    FaTimes, FaPrint, FaClipboardCheck, FaWarehouse, FaTag,
    FaShoppingBag, FaUsers, FaBuilding
} from 'react-icons/fa';
import axios from 'axios';

// Base URL configuration
const API_BASE_URL = import.meta.env.VITE_API_URL;

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add authorization token interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// API Functions
const ordersAPI = {
    list: async (params = {}) => {
        return api.get('/orders', { params });
    },
    get: async (id) => {
        return api.get(`/orders/${id}`);
    },
    place: async (data) => {
        return api.post('/orders/place', data);
    },
    updateStatus: async (id, status) => {
        return api.put(`/orders/${id}/status`, { status });
    },
    cancel: async (id) => {
        return api.post(`/orders/${id}/cancel`);
    },
    delete: async (id) => {
        return api.delete(`/orders/${id}`);
    }
};

// Additional APIs for getting names from IDs
const usersAPI = {
    get: async (id) => {
        return api.get(`/users/${id}`);
    },
    list: async (params = {}) => {
        return api.get('/users', { params });
    }
};

const vendorsAPI = {
    get: async (id) => {
        return api.get(`/vendors/${id}`);
    },
    list: async (params = {}) => {
        return api.get('/vendors', { params });
    }
};

const storesAPI = {
    get: async (id) => {
        return api.get(`/stores/${id}`);
    },
    list: async (params = {}) => {
        return api.get('/stores', { params });
    }
};

const productsAPI = {
    get: async (id) => {
        return api.get(`/products/${id}`);
    },
    list: async (params = {}) => {
        return api.get('/products', { params });
    }
};

const variantsAPI = {
    get: async (id) => {
        return api.get(`/variants/${id}`);
    },
    list: async (params = {}) => {
        return api.get('/variants', { params });
    }
};

// Cart API Functions for admin order creation
const cartAPI = {
    addItem: async (data) => {
        return api.post('/cart/items', data);
    },
    get: async () => {
        return api.get('/cart');
    },
    updateItem: async (id, data) => {
        return api.put(`/cart/items/${id}`, data);
    },
    removeItem: async (id) => {
        return api.delete(`/cart/items/${id}`);
    },
    clear: async () => {
        return api.delete('/cart');
    },
    checkout: async (data) => {
        return api.post('/cart/checkout', data);
    }
};

const categoriesAPI = {
    list: async (params = {}) => {
        return api.get('/categories', { params });
    }
};

const subcategoriesAPI = {
    list: async (params = {}) => {
        return api.get('/subcategories', { params });
    }
};

const inventoryAPI = {
    list: async (params = {}) => {
        return api.get('/inventory', { params });
    }
};

// Debug Helper Function
const debugAPIResponse = (name, response) => {
    console.log(`=== ${name} API Response ===`);
    console.log('Full response:', response);
    console.log('Response data:', response.data);
    console.log('Response data.data:', response.data?.data);
    console.log('Response data.data.data:', response.data?.data?.data);
    console.log('Is array?', Array.isArray(response.data));
    console.log('Is array in data?', Array.isArray(response.data?.data));
    console.log('Is array in data.data?', Array.isArray(response.data?.data?.data));
    console.log('========================');
};

// Currency formatting function for Indian Rupee
const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(isNaN(numAmount) ? 0 : numAmount);
};

// Date formatting function (Indian format)
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (err) {
        return 'Invalid Date';
    }
};

// Order Details Modal Component
const OrderDetailsModal = ({ show, onHide, order }) => {
    const [orderDetails, setOrderDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userName, setUserName] = useState('');
    const [vendorName, setVendorName] = useState('');
    const [storeName, setStoreName] = useState('');
    const [loadingNames, setLoadingNames] = useState(false);

    useEffect(() => {
        if (show && order) {
            fetchOrderDetails();
        }
    }, [show, order]);

    const fetchOrderDetails = async () => {
        try {
            setLoading(true);
            setLoadingNames(true);
            const response = await ordersAPI.get(order.id);
            // Handle different API response structures
            const details = response.data?.data || response.data;
            setOrderDetails(details);
            setError('');

            // Fetch names for IDs
            if (details) {
                await fetchEntityNames(details);
            }
        } catch (err) {
            setError('Failed to load order details');
            console.error('Error fetching order details:', err);
        } finally {
            setLoading(false);
            setLoadingNames(false);
        }
    };

    const fetchEntityNames = async (details) => {
        try {
            // Fetch user name
            if (details.user_id) {
                try {
                    const userResponse = await usersAPI.get(details.user_id);
                    const userData = userResponse.data?.data || userResponse.data;
                    if (userData) {
                        setUserName(userData.username || userData.email || userData.first_name || details.user_id);
                    }
                } catch (err) {
                    console.log('Error fetching user details, using ID:', err);
                    setUserName(details.user_id);
                }
            }

            // Fetch vendor name
            if (details.vendor_id) {
                try {
                    const vendorResponse = await vendorsAPI.get(details.vendor_id);
                    const vendorData = vendorResponse.data?.data || vendorResponse.data;
                    if (vendorData) {
                        setVendorName(vendorData.name || vendorData.email || details.vendor_id);
                    }
                } catch (err) {
                    console.log('Error fetching vendor details, using ID:', err);
                    setVendorName(details.vendor_id);
                }
            }

            // Fetch store name
            if (details.store_id) {
                try {
                    const storeResponse = await storesAPI.get(details.store_id);
                    const storeData = storeResponse.data?.data || storeResponse.data;
                    if (storeData) {
                        setStoreName(storeData.name || details.store_id);
                    }
                } catch (err) {
                    console.log('Error fetching store details, using ID:', err);
                    setStoreName(details.store_id);
                }
            }
        } catch (err) {
            console.error('Error fetching entity names:', err);
        }
    };

    const getStatusVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'placed': return 'primary';
            case 'confirmed': return 'info';
            case 'processing': return 'warning';
            case 'shipped': return 'warning';
            case 'delivered': return 'success';
            case 'cancelled': return 'danger';
            case 'refunded': return 'secondary';
            default: return 'secondary';
        }
    };

    if (loading) {
        return (
            <Modal show={show} onHide={onHide} size="lg" centered>
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" role="status" variant="primary">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading order details...</p>
                </Modal.Body>
            </Modal>
        );
    }

    return (
        <Modal show={show} onHide={onHide} size="xl" centered>
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title className="fw-bold">
                    Order Details: {orderDetails?.order_number || orderDetails?.id?.substring(0, 12) || 'N/A'}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {error && <Alert variant="danger" className="mb-4">{error}</Alert>}

                {orderDetails && (
                    <>
                        {/* Order Status Bar */}
                        <div className="d-flex align-items-center justify-content-between mb-4 p-3 bg-light rounded">
                            <div className="d-flex align-items-center">
                                <Badge
                                    bg={getStatusVariant(orderDetails.status)}
                                    className="fs-6 px-3 py-2 me-3"
                                >
                                    {(orderDetails.status || '').toUpperCase()}
                                </Badge>
                                <div>
                                    <h5 className="mb-0 fw-bold">{orderDetails.order_number || orderDetails.id || 'N/A'}</h5>
                                    <small className="text-muted">
                                        Placed on {formatDate(orderDetails.placed_at)}
                                    </small>
                                </div>
                            </div>
                            <div className="text-end">
                                <h4 className="text-success fw-bold mb-0">
                                    {formatCurrency(orderDetails.total || orderDetails.total_amount)}
                                </h4>
                                <small className="text-muted">Total Amount</small>
                            </div>
                        </div>

                        {/* Order Information Grid */}
                        <Row className="g-3 mb-4">
                            <Col md={4}>
                                <Card className="h-100 border">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                                            <FaUsers className="me-2 text-primary" />
                                            Customer Information
                                        </h6>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Customer Name</small>
                                            <strong className="d-flex align-items-center">
                                                <FaUser className="me-2 text-muted" size={14} />
                                                {loadingNames ? (
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                ) : (
                                                    userName || orderDetails.user_id || 'Guest Customer'
                                                )}
                                            </strong>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Customer ID</small>
                                            <code>{orderDetails.user_id || 'N/A'}</code>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={4}>
                                <Card className="h-100 border">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                                            <FaBuilding className="me-2 text-info" />
                                            Store & Vendor
                                        </h6>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Store</small>
                                            <strong className="d-flex align-items-center">
                                                <FaStore className="me-2 text-muted" size={14} />
                                                {loadingNames ? (
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                ) : (
                                                    storeName || orderDetails.store_id || 'N/A'
                                                )}
                                            </strong>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Vendor</small>
                                            <strong className="d-flex align-items-center">
                                                <FaBuilding className="me-2 text-muted" size={14} />
                                                {loadingNames ? (
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                ) : (
                                                    vendorName || orderDetails.vendor_id || 'N/A'
                                                )}
                                            </strong>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={4}>
                                <Card className="h-100 border">
                                    <Card.Body>
                                        <h6 className="fw-bold mb-3 d-flex align-items-center">
                                            <FaReceipt className="me-2 text-success" />
                                            Order Information
                                        </h6>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Order ID</small>
                                            <code>{orderDetails.id || 'N/A'}</code>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Order Number</small>
                                            <strong>{orderDetails.order_number || 'N/A'}</strong>
                                        </div>
                                        <div className="mb-2">
                                            <small className="text-muted d-block">Status</small>
                                            <Badge bg={getStatusVariant(orderDetails.status)} className="text-capitalize">
                                                {orderDetails.status || 'Unknown'}
                                            </Badge>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Order Items Table */}
                        <Card className="border mb-4">
                            <Card.Header className="bg-light">
                                <h6 className="mb-0 fw-bold d-flex align-items-center">
                                    <FaShoppingBag className="me-2" />
                                    Order Items ({orderDetails.items?.length || 0})
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="mb-0">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="py-3 ps-4">Product</th>
                                                <th className="py-3">Variant/SKU</th>
                                                <th className="py-3 text-end">Price</th>
                                                <th className="py-3 text-center">Quantity</th>
                                                <th className="py-3 pe-4 text-end">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {orderDetails.items && orderDetails.items.length > 0 ? (
                                                orderDetails.items.map((item, index) => (
                                                    <tr key={item.id} className="border-top">
                                                        <td className="ps-4">
                                                            <div className="fw-bold">{item.product_name || 'Unknown Product'}</div>
                                                            <small className="text-muted">Item ID: {item.id?.substring(0, 8)}...</small>
                                                        </td>
                                                        <td>
                                                            <div>
                                                                <div>SKU: <code>{item.sku || 'N/A'}</code></div>
                                                                <small className="text-muted">
                                                                    Variant: {item.product_variant_id?.substring(0, 8)}...
                                                                </small>
                                                            </div>
                                                        </td>
                                                        <td className="text-end">
                                                            <div className="fw-bold">{formatCurrency(item.price)}</div>
                                                            <small className="text-muted">per unit</small>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge bg="info" className="px-3 py-2 fs-6">
                                                                {item.quantity || 0}
                                                            </Badge>
                                                        </td>
                                                        <td className="pe-4 text-end">
                                                            <div className="fw-bold fs-5">{formatCurrency(item.total)}</div>
                                                            <small className="text-muted">
                                                                {item.quantity} × {formatCurrency(item.price)}
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4">
                                                        <div className="text-muted">
                                                            <FaBox className="fs-1 mb-3 opacity-25" />
                                                            <h6>No items found in this order</h6>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>

                        {/* Order Summary */}
                        <Row>
                            <Col md={8}>
                                {orderDetails.notes && (
                                    <Card className="border">
                                        <Card.Header className="bg-light">
                                            <h6 className="mb-0 fw-bold">Order Notes</h6>
                                        </Card.Header>
                                        <Card.Body>
                                            <p className="mb-0">{orderDetails.notes || 'No notes available'}</p>
                                        </Card.Body>
                                    </Card>
                                )}
                            </Col>
                            <Col md={4}>
                                <Card className="border">
                                    <Card.Header className="bg-light">
                                        <h6 className="mb-0 fw-bold d-flex align-items-center">
                                            <FaReceipt className="me-2" />
                                            Payment Summary
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-2 d-flex justify-content-between">
                                            <span className="text-muted">Subtotal:</span>
                                            <span>{formatCurrency(orderDetails.subtotal || orderDetails.subtotal_amount)}</span>
                                        </div>
                                        <div className="mb-2 d-flex justify-content-between">
                                            <span className="text-muted">Shipping:</span>
                                            <span>{formatCurrency(orderDetails.shipping || 0)}</span>
                                        </div>
                                        <div className="mb-2 d-flex justify-content-between">
                                            <span className="text-muted">Tax:</span>
                                            <span>{formatCurrency(orderDetails.tax || 0)}</span>
                                        </div>
                                        <div className="mb-2 d-flex justify-content-between text-danger">
                                            <span className="text-muted">Discount:</span>
                                            <span>-{formatCurrency(orderDetails.discount || 0)}</span>
                                        </div>
                                        <hr />
                                        <div className="d-flex justify-content-between fw-bold fs-5">
                                            <span>Total:</span>
                                            <span className="text-success">
                                                {formatCurrency(orderDetails.total || orderDetails.total_amount)}
                                            </span>
                                        </div>
                                        <div className="mt-2 d-flex justify-content-between">
                                            <small className="text-muted">Currency:</small>
                                            <small className="fw-bold">{orderDetails.currency || 'INR'}</small>
                                        </div>
                                        <div className="mt-1 d-flex justify-content-between">
                                            <small className="text-muted">Placed:</small>
                                            <small className="text-muted">{formatDate(orderDetails.placed_at)}</small>
                                        </div>
                                        {orderDetails.updated_at && (
                                            <div className="mt-1 d-flex justify-content-between">
                                                <small className="text-muted">Last Updated:</small>
                                                <small className="text-muted">{formatDate(orderDetails.updated_at)}</small>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top">
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <div>
                        <small className="text-muted">
                            Order ID: <code>{orderDetails?.id?.substring(0, 12)}...</code>
                        </small>
                    </div>
                    <div>
                        <Button variant="outline-secondary" onClick={onHide} className="me-2">
                            Close
                        </Button>
                        <Button variant="primary" onClick={() => window.print()} className="d-flex align-items-center">
                            <FaPrint className="me-2" />
                            Print Receipt
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

// Create Order Modal Component
const OrderCreateModal = ({ show, onHide, onSuccess }) => {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Data states
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [variants, setVariants] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [stores, setStores] = useState([]);

    // Selection states
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedSubcategory, setSelectedSubcategory] = useState('');
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedVariant, setSelectedVariant] = useState('');
    const [selectedStore, setSelectedStore] = useState('');
    const [quantity, setQuantity] = useState(1);

    // Loading states for cascading dropdowns
    const [loadingSubcategories, setLoadingSubcategories] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [loadingVariants, setLoadingVariants] = useState(false);

    // Cart state
    const [cart, setCart] = useState([]);
    const [cartTotal, setCartTotal] = useState(0);
    const [cartItems, setCartItems] = useState([]); // For storing actual cart item IDs from API
    const [checkoutData, setCheckoutData] = useState(null); // Store checkout response data

    // Order details
    const [customerId, setCustomerId] = useState('');
    const [shippingFee, setShippingFee] = useState('0');
    const [taxRate, setTaxRate] = useState('0');
    const [discount, setDiscount] = useState('0');
    const [notes, setNotes] = useState('');

    useEffect(() => {
        if (show) {
            fetchCategories();
            fetchStores();
            resetForm();
            // Clear any existing cart when modal opens
            clearExistingCart();
        }
    }, [show]);

    useEffect(() => {
        if (selectedCategory) {
            fetchSubcategoriesByCategory(selectedCategory);
        } else {
            setSubcategories([]);
            setSelectedSubcategory('');
            setSelectedProduct('');
            setSelectedVariant('');
            setProducts([]);
            setVariants([]);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (selectedSubcategory) {
            fetchProductsBySubcategory(selectedSubcategory);
        } else {
            setSelectedProduct('');
            setSelectedVariant('');
            setProducts([]);
            setVariants([]);
        }
    }, [selectedSubcategory]);

    useEffect(() => {
        if (selectedProduct) {
            fetchVariantsByProduct(selectedProduct);
        } else {
            setSelectedVariant('');
            setVariants([]);
        }
    }, [selectedProduct]);

    useEffect(() => {
        if (selectedVariant) {
            fetchInventoryByVariant(selectedVariant);
        } else {
            setInventory([]);
        }
    }, [selectedVariant]);

    useEffect(() => {
        calculateCartTotal();
    }, [cart]);

    const clearExistingCart = async () => {
        try {
            // Clear admin's cart before creating new order
            await cartAPI.clear();
            setCartItems([]);
            setCheckoutData(null);
        } catch (err) {
            console.log('No existing cart or error clearing cart:', err);
            // Continue anyway
        }
    };

    const resetForm = () => {
        setStep(1);
        setSelectedCategory('');
        setSelectedSubcategory('');
        setSelectedProduct('');
        setSelectedVariant('');
        setSelectedStore('');
        setQuantity(1);
        setCart([]);
        setCartItems([]);
        setCartTotal(0);
        setCheckoutData(null);
        setCustomerId('');
        setShippingFee('0');
        setTaxRate('0');
        setDiscount('0');
        setNotes('');
        setError('');
        setCategories([]);
        setSubcategories([]);
        setProducts([]);
        setVariants([]);
        setInventory([]);
        setLoadingSubcategories(false);
        setLoadingProducts(false);
        setLoadingVariants(false);
    };

    const fetchCategories = async () => {
        try {
            const response = await categoriesAPI.list({ active_only: true, limit: 100 });
            console.log('Categories API Response:', response.data);

            // Extract categories array from response
            let categoriesData = [];
            if (response.data?.data?.data) {
                categoriesData = response.data.data.data;
            } else if (response.data?.data) {
                categoriesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                categoriesData = response.data;
            }

            setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } catch (err) {
            console.error('Error fetching categories:', err);
            setCategories([]);
        }
    };

    const fetchSubcategoriesByCategory = async (categoryId) => {
        try {
            setLoadingSubcategories(true);
            console.log('Fetching subcategories for category:', categoryId);

            const response = await subcategoriesAPI.list({
                category_id: categoryId,
                active_only: true,
                limit: 100
            });

            console.log('Subcategories API Response:', response.data);

            // Extract subcategories array from response
            let subcategoriesData = [];
            if (response.data?.data?.data) {
                subcategoriesData = response.data.data.data;
            } else if (response.data?.data) {
                subcategoriesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                subcategoriesData = response.data;
            }

            console.log('Extracted subcategories:', subcategoriesData);
            setSubcategories(Array.isArray(subcategoriesData) ? subcategoriesData : []);
            setLoadingSubcategories(false);

            // Clear dependent selections
            setSelectedSubcategory('');
            setSelectedProduct('');
            setSelectedVariant('');
            setProducts([]);
            setVariants([]);

        } catch (err) {
            console.error('Error fetching subcategories:', err);
            setSubcategories([]);
            setLoadingSubcategories(false);
        }
    };

    const fetchProductsBySubcategory = async (subcategoryId) => {
        try {
            setLoadingProducts(true);
            console.log('Fetching products for subcategory:', subcategoryId);

            // Use the products API with subcategory_id filter
            const response = await api.get('/products', {
                params: {
                    subcategory_id: subcategoryId,
                    is_active: 1,
                    limit: 100
                }
            });

            console.log('Products API Response:', response.data);

            // Extract products array from response
            let productsData = [];
            if (response.data?.data?.data) {
                productsData = response.data.data.data;
            } else if (response.data?.data) {
                productsData = response.data.data;
            } else if (Array.isArray(response.data)) {
                productsData = response.data;
            }

            console.log('Extracted products:', productsData);

            // Filter products by subcategory_id (in case API doesn't filter properly)
            let filteredProducts = [];
            if (Array.isArray(productsData)) {
                filteredProducts = productsData.filter(product =>
                    product.subcategory_id === subcategoryId
                );
            }

            console.log('Filtered products for subcategory:', subcategoryId, ':', filteredProducts);
            setProducts(filteredProducts);
            setLoadingProducts(false);

            // Clear dependent selections
            setSelectedProduct('');
            setSelectedVariant('');
            setVariants([]);

        } catch (err) {
            console.error('Error fetching products:', err);
            setProducts([]);
            setLoadingProducts(false);
        }
    };

    const fetchVariantsByProduct = async (productId) => {
        try {
            setLoadingVariants(true);
            console.log('Fetching variants for product:', productId);

            // First, try the product-specific endpoint
            let response;
            let endpointUsed = '';

            try {
                response = await api.get(`/products/${productId}/variants`, {
                    params: { limit: 100 }
                });
                endpointUsed = 'product-specific';
                debugAPIResponse('Product Variants', response);
            } catch (err) {
                console.log('Product variants endpoint failed, trying general variants endpoint');
                // Fallback to general variants endpoint
                response = await api.get('/variants', {
                    params: {
                        product_id: productId,
                        limit: 100
                    }
                });
                endpointUsed = 'general';
                debugAPIResponse('General Variants', response);
            }

            console.log(`Used ${endpointUsed} endpoint`);

            // Handle different API response structures
            let variantsData = [];

            if (response.data?.status === 'success') {
                // Structure: { status: "success", data: { data: [], meta: {} } }
                if (response.data.data?.data) {
                    variantsData = response.data.data.data;
                } else if (response.data.data) {
                    variantsData = response.data.data; // Could be array directly
                }
            } else if (response.data?.data) {
                // Structure: { data: [], meta: {} }
                if (Array.isArray(response.data.data)) {
                    variantsData = response.data.data;
                } else if (response.data.data?.data) {
                    variantsData = response.data.data.data;
                }
            } else if (Array.isArray(response.data)) {
                // Direct array response
                variantsData = response.data;
            }

            console.log('Extracted variants data:', variantsData);

            // Filter variants by product_id (in case API doesn't filter properly)
            let filteredVariants = [];
            if (Array.isArray(variantsData)) {
                filteredVariants = variantsData.filter(variant =>
                    variant && variant.product_id === productId
                );
            } else {
                // If variantsData is not an array, try to convert it
                if (variantsData && typeof variantsData === 'object') {
                    const values = Object.values(variantsData);
                    const arrayFromObject = values.find(val => Array.isArray(val));
                    if (arrayFromObject) {
                        filteredVariants = arrayFromObject.filter(variant =>
                            variant && variant.product_id === productId
                        );
                    }
                }
            }

            console.log('Filtered variants for product:', productId, ':', filteredVariants);
            setVariants(filteredVariants || []);

            // Clear dependent selection
            setSelectedVariant('');
            setError('');

        } catch (err) {
            console.error('Error fetching variants:', err);
            setError('Failed to load variants. Please try again.');
            setVariants([]);
        } finally {
            setLoadingVariants(false);
        }
    };

    const fetchInventoryByVariant = async (variantId) => {
        try {
            const response = await api.get('/inventory', {
                params: { limit: 100 }
            });
            console.log('Inventory API Response:', response.data);

            // Extract inventory array from response
            let inventoryData = [];
            if (response.data?.data?.data) {
                inventoryData = response.data.data.data;
            } else if (response.data?.data) {
                inventoryData = response.data.data;
            } else if (Array.isArray(response.data)) {
                inventoryData = response.data;
            }

            const allInventory = Array.isArray(inventoryData) ? inventoryData : [];
            const variantInventory = allInventory.filter(
                item => item.product_variant_id === variantId
            );
            setInventory(variantInventory);
        } catch (err) {
            console.error('Error fetching inventory:', err);
            setInventory([]);
        }
    };

    const fetchStores = async () => {
        try {
            const response = await api.get('/stores', {
                params: { limit: 100 }
            });
            console.log('Stores API Response:', response.data);

            // Extract stores array from response
            let storesData = [];
            if (response.data?.data?.data) {
                storesData = response.data.data.data;
            } else if (response.data?.data) {
                storesData = response.data.data;
            } else if (Array.isArray(response.data)) {
                storesData = response.data;
            }

            console.log('Extracted stores data:', storesData);

            // If storesData is not an array, try to extract it
            if (!Array.isArray(storesData) && storesData && typeof storesData === 'object') {
                const values = Object.values(storesData);
                storesData = values.find(val => Array.isArray(val)) || [];
            }

            setStores(Array.isArray(storesData) ? storesData : []);
        } catch (err) {
            console.error('Error fetching stores:', err);
            setStores([]);
        }
    };

    const calculateCartTotal = () => {
        const total = cart.reduce((sum, item) => {
            return sum + (parseFloat(item.price) || 0) * (item.quantity || 0);
        }, 0);
        setCartTotal(total || 0);
    };

    const handleAddToCart = async () => {
        if (!selectedVariant || quantity < 1) {
            setError('Please select a variant and quantity');
            return;
        }

        const variant = variants.find(v => v.id === selectedVariant);
        if (!variant) {
            setError('Selected variant not found. Please refresh and try again.');
            return;
        }

        // Check if variant has required properties
        if (!variant.price) {
            setError('Variant price information is missing');
            return;
        }

        // Check inventory if store is selected
        if (selectedStore) {
            const inventoryItem = inventory.find(i => i.store_id === selectedStore);
            if (inventoryItem && quantity > inventoryItem.quantity) {
                setError(`Only ${inventoryItem.quantity} items available in stock at selected store`);
                return;
            }
        }

        const existingItemIndex = cart.findIndex(item => item.variantId === selectedVariant);
        const product = products.find(p => p.id === selectedProduct);
        const category = categories.find(c => c.id === selectedCategory);
        const subcategory = subcategories.find(s => s.id === selectedSubcategory);

        const cartItem = {
            variantId: selectedVariant,
            sku: variant.sku || 'N/A',
            name: product?.name || 'Unknown Product',
            variantName: variant.title || 'Standard',
            category: category?.name || 'Uncategorized',
            subcategory: subcategory?.name || 'No Subcategory',
            price: parseFloat(variant.price) || 0,
            quantity: parseInt(quantity) || 1,
            attributes: variant.attributes || {}
        };

        try {
            // Add item to actual cart via API
            const response = await cartAPI.addItem({
                product_variant_id: selectedVariant,
                quantity: parseInt(quantity) || 1
            });

            console.log('Added to cart API response:', response.data);

            // Get the cart item ID from response if available
            const cartItemId = response.data?.id || response.data?.data?.id;
            if (cartItemId) {
                cartItem.cartItemId = cartItemId;
                // Store cart item ID for later use
                setCartItems(prev => [...prev, { variantId: selectedVariant, cartItemId }]);
            }

            if (existingItemIndex > -1) {
                const updatedCart = [...cart];
                updatedCart[existingItemIndex].quantity += parseInt(quantity);
                setCart(updatedCart);
            } else {
                setCart([...cart, cartItem]);
            }

            setQuantity(1);
            setError('');
        } catch (err) {
            console.error('Error adding to cart:', err);
            setError(err.response?.data?.message || 'Failed to add item to cart. Please try again.');
        }
    };

    const handleRemoveFromCart = async (index) => {
        const itemToRemove = cart[index];

        try {
            // Find the cart item ID for this variant
            const cartItem = cartItems.find(item => item.variantId === itemToRemove.variantId);

            if (cartItem && cartItem.cartItemId) {
                // Remove from actual cart via API
                await cartAPI.removeItem(cartItem.cartItemId);

                // Remove from cartItems state
                setCartItems(prev => prev.filter(item => item.variantId !== itemToRemove.variantId));
            }

            // Remove from local cart state
            const updatedCart = [...cart];
            updatedCart.splice(index, 1);
            setCart(updatedCart);
        } catch (err) {
            console.error('Error removing from cart:', err);
            setError('Failed to remove item from cart. Please try again.');
        }
    };

    const handleCheckout = async () => {
        if (cart.length === 0) {
            setError('Please add items to cart before checkout');
            return;
        }

        if (!customerId.trim()) {
            setError('Please enter a customer ID');
            return;
        }

        if (!selectedStore) {
            setError('Please select a store');
            return;
        }

        setLoading(true);
        try {
            // Step 1: Call checkout API to prepare cart for order
            const checkoutResponse = await cartAPI.checkout({
                store_id: selectedStore
            });

            console.log('Checkout API response:', checkoutResponse.data);

            if (checkoutResponse.data?.status === 'success') {
                setCheckoutData(checkoutResponse.data.data);
                setStep(3); // Move to confirmation step
                setError('');
            } else {
                setError(checkoutResponse.data?.message || 'Checkout failed. Please try again.');
            }
        } catch (err) {
            console.error('Error during checkout:', err);
            setError(err.response?.data?.message || 'Failed to checkout. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePlaceOrder = async () => {
        if (!checkoutData) {
            setError('No checkout data available. Please complete checkout first.');
            return;
        }

        setLoading(true);
        try {
            // Calculate order totals
            const subtotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            const shipping = parseFloat(shippingFee) || 0;
            const tax = (subtotal * (parseFloat(taxRate) || 0)) / 100;
            const discountAmount = parseFloat(discount) || 0;
            const total = subtotal + shipping + tax - discountAmount;

            // Step 2: Call place order API with cart_id from checkout response
            const orderData = {
                store_id: selectedStore,
                shipping: shipping.toFixed(2),
                tax: tax.toFixed(2),
                discount: discountAmount.toFixed(2),
                currency: 'INR',
                notes: notes.trim(),
                // Include cart_id if available in checkout response
                cart_id: checkoutData.cart_id
            };

            console.log('Placing order with data:', orderData);

            const response = await ordersAPI.place(orderData);
            console.log('Place order API response:', response);

            if (response.data?.status === 'success' || response.status === 200 || response.status === 201) {
                onSuccess();
                resetForm();
                onHide();

                // Clear the cart after successful order
                await clearExistingCart();
            } else {
                setError(response.data?.message || 'Failed to place order');
            }
        } catch (err) {
            console.error('Error placing order:', err);
            setError(err.response?.data?.message || 'Failed to place order. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
            <Modal.Header closeButton className="border-bottom bg-light">
                <Modal.Title className="fw-bold">
                    Create New Order
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-4">
                {/* Progress Steps */}
                <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between position-relative">
                        <div className="position-absolute top-50 start-0 end-0 translate-middle-y"
                            style={{ height: '2px', backgroundColor: '#e9ecef', zIndex: 1 }}></div>

                        {[1, 2, 3].map((stepNum) => (
                            <div key={stepNum} className="d-flex flex-column align-items-center position-relative" style={{ zIndex: 2 }}>
                                <div className={`rounded-circle d-flex align-items-center justify-content-center 
                                    ${step >= stepNum ? 'bg-primary text-white' : 'bg-light text-muted border'}`}
                                    style={{ width: '40px', height: '40px', fontSize: '1.1rem', fontWeight: 'bold' }}>
                                    {stepNum}
                                </div>
                                <span className={`mt-2 small ${step >= stepNum ? 'text-primary fw-bold' : 'text-muted'}`}>
                                    {stepNum === 1 && 'Select Products'}
                                    {stepNum === 2 && 'Order Details'}
                                    {stepNum === 3 && 'Place Order'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {error && (
                    <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                        {error}
                    </Alert>
                )}

                {/* Step 1: Product Selection */}
                {step === 1 && (
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border h-100">
                                <Card.Body>
                                    <h5 className="fw-bold mb-4 d-flex align-items-center">
                                        <FaShoppingCart className="me-2 text-primary" />
                                        Select Products
                                    </h5>

                                    <Row className="g-3">
                                        {/* Category Dropdown */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Category *</Form.Label>
                                                <Form.Select
                                                    value={selectedCategory}
                                                    onChange={(e) => {
                                                        const newCategory = e.target.value;
                                                        setSelectedCategory(newCategory);
                                                        setSelectedSubcategory('');
                                                        setSelectedProduct('');
                                                        setSelectedVariant('');
                                                        setProducts([]);
                                                        setVariants([]);
                                                    }}
                                                    size="lg"
                                                    disabled={loading}
                                                >
                                                    <option value="">Select Category</option>
                                                    {Array.isArray(categories) && categories.map(cat => (
                                                        <option key={cat.id} value={cat.id}>
                                                            {cat.name}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {!selectedCategory && (
                                                    <Form.Text className="text-muted">
                                                        Select a category to see subcategories
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* Subcategory Dropdown - Dependent on Category */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Subcategory</Form.Label>
                                                <div className="position-relative">
                                                    <Form.Select
                                                        value={selectedSubcategory}
                                                        onChange={(e) => {
                                                            const newSubcategory = e.target.value;
                                                            setSelectedSubcategory(newSubcategory);
                                                            setSelectedProduct('');
                                                            setSelectedVariant('');
                                                            setVariants([]);
                                                        }}
                                                        disabled={!selectedCategory || loadingSubcategories}
                                                        size="lg"
                                                    >
                                                        <option value="">Select Subcategory</option>
                                                        {loadingSubcategories ? (
                                                            <option value="" disabled>Loading subcategories...</option>
                                                        ) : (
                                                            Array.isArray(subcategories) && subcategories.length > 0 ? (
                                                                subcategories.map(sub => (
                                                                    <option key={sub.id} value={sub.id}>
                                                                        {sub.name}
                                                                    </option>
                                                                ))
                                                            ) : (
                                                                <option value="" disabled>No subcategories found</option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                    {loadingSubcategories && (
                                                        <div className="position-absolute top-50 end-3 translate-middle-y">
                                                            <Spinner animation="border" size="sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                {selectedCategory && !loadingSubcategories && subcategories.length === 0 && (
                                                    <Form.Text className="text-warning">
                                                        No subcategories found for this category
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* Product Dropdown - Dependent on Subcategory */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Product</Form.Label>
                                                <div className="position-relative">
                                                    <Form.Select
                                                        value={selectedProduct}
                                                        onChange={(e) => {
                                                            const newProduct = e.target.value;
                                                            setSelectedProduct(newProduct);
                                                            setSelectedVariant('');
                                                        }}
                                                        disabled={!selectedSubcategory || loadingProducts}
                                                        size="lg"
                                                    >
                                                        <option value="">Select Product</option>
                                                        {loadingProducts ? (
                                                            <option value="" disabled>Loading products...</option>
                                                        ) : (
                                                            Array.isArray(products) && products.length > 0 ? (
                                                                products.map(prod => (
                                                                    <option key={prod.id} value={prod.id}>
                                                                        {prod.name}
                                                                    </option>
                                                                ))
                                                            ) : (
                                                                <option value="" disabled>No products found</option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                    {loadingProducts && (
                                                        <div className="position-absolute top-50 end-3 translate-middle-y">
                                                            <Spinner animation="border" size="sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                {selectedSubcategory && !loadingProducts && products.length === 0 && (
                                                    <Form.Text className="text-warning">
                                                        No products found for this subcategory
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* Variant Dropdown - Dependent on Product */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Variant *</Form.Label>
                                                <div className="position-relative">
                                                    <Form.Select
                                                        value={selectedVariant}
                                                        onChange={(e) => {
                                                            const variantId = e.target.value;
                                                            setSelectedVariant(variantId);

                                                            // Find the selected variant to get price for display
                                                            const selected = variants.find(v => v.id === variantId);
                                                            if (selected) {
                                                                console.log('Selected variant:', selected);
                                                            }
                                                        }}
                                                        disabled={!selectedProduct || loadingVariants}
                                                        size="lg"
                                                    >
                                                        <option value="">Select Variant</option>
                                                        {loadingVariants ? (
                                                            <option value="" disabled>
                                                                Loading variants...
                                                            </option>
                                                        ) : (
                                                            variants.length > 0 ? (
                                                                variants.map(variant => (
                                                                    <option
                                                                        key={variant.id}
                                                                        value={variant.id}
                                                                        data-price={variant.price}
                                                                        data-title={variant.title}
                                                                        data-attributes={JSON.stringify(variant.attributes || {})}
                                                                    >
                                                                        {variant.title || 'Standard'} - {formatCurrency(variant.price || 0)}
                                                                        {variant.attributes && Object.keys(variant.attributes).length > 0 && (
                                                                            <small className="text-muted ms-2">
                                                                                ({Object.entries(variant.attributes).map(([k, v]) => `${k}: ${v}`).join(', ')})
                                                                            </small>
                                                                        )}
                                                                    </option>
                                                                ))
                                                            ) : (
                                                                <option value="" disabled>
                                                                    {selectedProduct ? 'No variants available' : 'Select a product first'}
                                                                </option>
                                                            )
                                                        )}
                                                    </Form.Select>
                                                    {loadingVariants && (
                                                        <div className="position-absolute top-50 end-3 translate-middle-y">
                                                            <Spinner animation="border" size="sm" />
                                                        </div>
                                                    )}
                                                </div>
                                                {selectedProduct && !loadingVariants && variants.length === 0 && (
                                                    <Alert variant="warning" className="mt-2 py-2">
                                                        <FaInfoCircle className="me-2" />
                                                        This product has no variants. Please create variants first.
                                                    </Alert>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* Store Dropdown - Shows only stores with inventory for selected variant */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Store *</Form.Label>
                                                <Form.Select
                                                    value={selectedStore}
                                                    onChange={(e) => setSelectedStore(e.target.value)}
                                                    disabled={!selectedVariant}
                                                    size="lg"
                                                >
                                                    <option value="">Select Store</option>
                                                    {stores.map(store => (
                                                        <option key={store.id || store} value={store.id || store}>
                                                            {store.name || store.id || store}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                {!selectedVariant && (
                                                    <Form.Text className="text-muted">
                                                        Select a variant to see available stores
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>

                                        {/* Quantity Input */}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Quantity *</Form.Label>
                                                <InputGroup size="lg">
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                                        disabled={quantity <= 1}
                                                    >
                                                        <FaTimes />
                                                    </Button>
                                                    <Form.Control
                                                        type="number"
                                                        value={quantity}
                                                        onChange={(e) => {
                                                            const val = parseInt(e.target.value) || 1;
                                                            setQuantity(Math.max(1, val));
                                                        }}
                                                        min="1"
                                                        className="text-center"
                                                    />
                                                    <Button
                                                        variant="outline-secondary"
                                                        onClick={() => setQuantity(quantity + 1)}
                                                    >
                                                        <FaPlus />
                                                    </Button>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="mt-4 pt-3 border-top">
                                        <Button
                                            variant="primary"
                                            onClick={handleAddToCart}
                                            disabled={!selectedVariant || !selectedStore || quantity < 1 || loading}
                                            size="lg"
                                            className="w-100 py-3"
                                        >
                                            <FaPlus className="me-2" />
                                            Add to Cart
                                        </Button>
                                        {(!selectedVariant || !selectedStore) && (
                                            <Form.Text className="text-muted text-center d-block mt-2">
                                                Select a variant and store to add to cart
                                            </Form.Text>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        {/* Shopping Cart Sidebar */}
                        <Col lg={4}>
                            <Card className="border h-100">
                                <Card.Body className="d-flex flex-column">
                                    <h5 className="fw-bold mb-4 d-flex align-items-center">
                                        <FaBox className="me-2 text-success" />
                                        Shopping Cart ({cart.length})
                                    </h5>

                                    {cart.length === 0 ? (
                                        <div className="text-center py-5 my-auto">
                                            <div className="bg-light rounded-circle d-inline-flex p-4 mb-3">
                                                <FaShoppingCart className="text-muted fs-1" />
                                            </div>
                                            <h6 className="text-muted">Your cart is empty</h6>
                                            <p className="text-muted small mb-0">Add products from the selection panel</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex-grow-1 overflow-auto" style={{ maxHeight: '400px' }}>
                                                {cart.map((item, index) => (
                                                    <div key={index} className="border rounded p-3 mb-3 bg-light">
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <div className="flex-grow-1">
                                                                <h6 className="fw-bold mb-1">{item.name}</h6>
                                                                <div className="mb-2">
                                                                    <Badge bg="info" className="me-1">{item.category}</Badge>
                                                                    <Badge bg="secondary">{item.subcategory}</Badge>
                                                                </div>
                                                                <div className="small text-muted">
                                                                    <div>Variant: {item.variantName}</div>
                                                                    <div>SKU: {item.sku}</div>
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => handleRemoveFromCart(index)}
                                                                className="ms-2"
                                                            >
                                                                <FaTrash />
                                                            </Button>
                                                        </div>
                                                        <div className="d-flex justify-content-between align-items-center mt-2 pt-2 border-top">
                                                            <div className="fw-bold">
                                                                {formatCurrency(item.price)} × {item.quantity}
                                                            </div>
                                                            <div className="fw-bold fs-5">
                                                                {formatCurrency(item.price * item.quantity)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="border-top pt-3 mt-3">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="text-muted">Subtotal:</span>
                                                    <span className="fw-bold">{formatCurrency(cartTotal)}</span>
                                                </div>
                                                <div className="d-flex justify-content-between text-success">
                                                    <span>Total:</span>
                                                    <span className="fw-bold fs-4">{formatCurrency(cartTotal)}</span>
                                                </div>

                                                <Button
                                                    variant="success"
                                                    className="w-100 mt-4 py-3"
                                                    onClick={() => setStep(2)}
                                                    disabled={cart.length === 0}
                                                >
                                                    <FaChevronRight className="me-2" />
                                                    Proceed to Checkout
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Step 2: Order Details */}
                {step === 2 && (
                    <Row className="g-4">
                        <Col lg={8}>
                            <Card className="border h-100">
                                <Card.Body>
                                    <h5 className="fw-bold mb-4 d-flex align-items-center">
                                        <FaClipboardCheck className="me-2 text-primary" />
                                        Order Details
                                    </h5>

                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Customer ID *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={customerId}
                                                    onChange={(e) => setCustomerId(e.target.value)}
                                                    placeholder="Enter customer ID or email"
                                                    size="lg"
                                                    required
                                                />
                                                <Form.Text className="text-muted">
                                                    Required for order assignment
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Store *</Form.Label>
                                                <Form.Select
                                                    value={selectedStore}
                                                    onChange={(e) => setSelectedStore(e.target.value)}
                                                    size="lg"
                                                    required
                                                >
                                                    <option value="">Select Store</option>
                                                    {stores.map(store => (
                                                        <option key={store.id || store} value={store.id || store}>
                                                            {store.name || store.id || store}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Store for order fulfillment
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Shipping Fee (₹)</Form.Label>
                                                <InputGroup size="lg">
                                                    <InputGroup.Text>₹</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={shippingFee}
                                                        onChange={(e) => setShippingFee(e.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Tax Rate (%)</Form.Label>
                                                <InputGroup size="lg">
                                                    <Form.Control
                                                        type="number"
                                                        value={taxRate}
                                                        onChange={(e) => setTaxRate(e.target.value)}
                                                        min="0"
                                                        max="100"
                                                        step="0.1"
                                                    />
                                                    <InputGroup.Text>%</InputGroup.Text>
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Discount (₹)</Form.Label>
                                                <InputGroup size="lg">
                                                    <InputGroup.Text>₹</InputGroup.Text>
                                                    <Form.Control
                                                        type="number"
                                                        value={discount}
                                                        onChange={(e) => setDiscount(e.target.value)}
                                                        min="0"
                                                        step="0.01"
                                                    />
                                                </InputGroup>
                                            </Form.Group>
                                        </Col>

                                        <Col md={12}>
                                            <Form.Group>
                                                <Form.Label className="fw-semibold">Order Notes</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={4}
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="Add any special instructions, delivery notes, or customer requests..."
                                                    size="lg"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={4}>
                            <Card className="border h-100">
                                <Card.Body>
                                    <h5 className="fw-bold mb-4">Order Summary</h5>

                                    <div className="mb-4">
                                        <h6 className="text-muted mb-3">Items ({cart.length})</h6>
                                        {cart.map((item, index) => (
                                            <div key={index} className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                                                <div className="text-truncate" style={{ maxWidth: '60%' }}>
                                                    <small className="d-block">{item.name}</small>
                                                    <small className="text-muted">
                                                        {item.quantity} × {formatCurrency(item.price)}
                                                    </small>
                                                </div>
                                                <small className="fw-bold">{formatCurrency(item.price * item.quantity)}</small>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="border-top pt-3">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Subtotal:</span>
                                            <span>{formatCurrency(cartTotal)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Shipping:</span>
                                            <span>{formatCurrency(parseFloat(shippingFee) || 0)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-muted">Tax ({taxRate}%):</span>
                                            <span>{formatCurrency((cartTotal * (parseFloat(taxRate) || 0)) / 100)}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-3 text-danger">
                                            <span className="text-muted">Discount:</span>
                                            <span>-{formatCurrency(parseFloat(discount) || 0)}</span>
                                        </div>

                                        <div className="d-flex justify-content-between border-top pt-2 fw-bold fs-4">
                                            <span>Total:</span>
                                            <span className="text-success">
                                                {formatCurrency(
                                                    cartTotal +
                                                    (parseFloat(shippingFee) || 0) +
                                                    ((cartTotal * (parseFloat(taxRate) || 0)) / 100) -
                                                    (parseFloat(discount) || 0)
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-4 d-grid gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setStep(1)}
                                            size="lg"
                                        >
                                            Back to Products
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={handleCheckout}
                                            size="lg"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Processing...
                                                </>
                                            ) : (
                                                'Proceed to Checkout'
                                            )}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}

                {/* Step 3: Confirmation and Place Order */}
                {step === 3 && (
                    <Row>
                        <Col lg={12}>
                            <Card className="border">
                                <Card.Body className="text-center py-5">
                                    <div className="mb-5">
                                        <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-4 mb-4">
                                            <FaClipboardCheck className="text-success fs-1" />
                                        </div>
                                        <h3 className="fw-bold mb-3">Ready to Create Order</h3>
                                        <p className="text-muted mb-4">
                                            Please review the order details below before confirming
                                        </p>
                                    </div>

                                    <Row className="justify-content-center mb-5">
                                        <Col lg={8}>
                                            <Card className="border shadow-sm">
                                                <Card.Body className="p-4">
                                                    <Row>
                                                        <Col md={6} className="text-start">
                                                            <h6 className="fw-bold mb-3 text-primary">Order Information</h6>
                                                            <div className="mb-3">
                                                                <small className="text-muted d-block">Customer</small>
                                                                <strong className="fs-5">{customerId || 'Not specified'}</strong>
                                                            </div>
                                                            <div className="mb-3">
                                                                <small className="text-muted d-block">Store</small>
                                                                <strong className="fs-5">{selectedStore || 'Not specified'}</strong>
                                                            </div>
                                                            <div className="mb-3">
                                                                <small className="text-muted d-block">Items</small>
                                                                <strong className="fs-5">{cart.length} products</strong>
                                                            </div>
                                                            {checkoutData?.cart_id && (
                                                                <div className="mb-3">
                                                                    <small className="text-muted d-block">Cart ID</small>
                                                                    <strong className="fs-6 text-success">{checkoutData.cart_id}</strong>
                                                                </div>
                                                            )}
                                                        </Col>
                                                        <Col md={6} className="text-start">
                                                            <h6 className="fw-bold mb-3 text-primary">Amount Summary</h6>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Subtotal:</small>
                                                                <div className="fw-bold">{formatCurrency(cartTotal)}</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Shipping:</small>
                                                                <div className="fw-bold">{formatCurrency(parseFloat(shippingFee) || 0)}</div>
                                                            </div>
                                                            <div className="mb-2">
                                                                <small className="text-muted">Tax:</small>
                                                                <div className="fw-bold">{formatCurrency((cartTotal * (parseFloat(taxRate) || 0)) / 100)}</div>
                                                            </div>
                                                            <div className="mb-2 text-danger">
                                                                <small className="text-muted">Discount:</small>
                                                                <div className="fw-bold">-{formatCurrency(parseFloat(discount) || 0)}</div>
                                                            </div>
                                                            <hr className="my-3" />
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <h5 className="mb-0 fw-bold">Total:</h5>
                                                                <h3 className="mb-0 text-success fw-bold">
                                                                    {formatCurrency(
                                                                        cartTotal +
                                                                        (parseFloat(shippingFee) || 0) +
                                                                        ((cartTotal * (parseFloat(taxRate) || 0)) / 100) -
                                                                        (parseFloat(discount) || 0)
                                                                    )}
                                                                </h3>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {checkoutData && checkoutData.message && (
                                        <Alert variant="info" className="mb-4">
                                            <FaInfoCircle className="me-2" />
                                            {checkoutData.message}
                                        </Alert>
                                    )}

                                    <div className="d-flex justify-content-center gap-3">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => setStep(2)}
                                            disabled={loading}
                                            size="lg"
                                            className="px-5"
                                        >
                                            Back
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={handlePlaceOrder}
                                            disabled={loading}
                                            size="lg"
                                            className="px-5"
                                        >
                                            {loading ? (
                                                <>
                                                    <Spinner animation="border" size="sm" className="me-2" />
                                                    Creating Order...
                                                </>
                                            ) : (
                                                'Confirm & Place Order'
                                            )}
                                        </Button>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top bg-light">
                <div className="d-flex justify-content-between w-100">
                    <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
                        Cancel
                    </Button>
                    <div>
                        {step > 1 && (
                            <Button variant="outline-primary" onClick={() => setStep(step - 1)} className="me-2">
                                Back
                            </Button>
                        )}
                        {step < 3 && step > 1 && (
                            <Button variant="primary" onClick={handleCheckout}>
                                Checkout
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

// Main Orders Component
const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [storeFilter, setStoreFilter] = useState('');

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);

    const [selectedOrder, setSelectedOrder] = useState(null);
    const [statusUpdate, setStatusUpdate] = useState('');

    const [stats, setStats] = useState({
        total: 0,
        placed: 0,
        confirmed: 0,
        shipped: 0,
        delivered: 0,
        cancelled: 0,
        revenue: 0
    });

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const response = await ordersAPI.list({ page: 1, limit: 100 });
            console.log('Orders API Response:', response.data);

            // Handle different API response structures
            let ordersData = [];
            if (response.data?.data?.data) {
                ordersData = response.data.data.data; // { data: { data: [], meta: {} } }
            } else if (response.data?.data) {
                ordersData = response.data.data; // { data: [], meta: {} }
            } else if (response.data) {
                ordersData = response.data; // direct array or object
            }

            // Ensure ordersData is an array
            if (Array.isArray(ordersData)) {
                setOrders(ordersData);
                setFilteredOrders(ordersData);
                calculateStats(ordersData);
            } else if (ordersData && typeof ordersData === 'object') {
                // Try to extract array from object
                const possibleArray = Object.values(ordersData).find(val => Array.isArray(val));
                if (Array.isArray(possibleArray)) {
                    setOrders(possibleArray);
                    setFilteredOrders(possibleArray);
                    calculateStats(possibleArray);
                } else {
                    setOrders([]);
                    setFilteredOrders([]);
                }
            } else {
                setOrders([]);
                setFilteredOrders([]);
            }

            setError('');
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Failed to fetch orders. Please check your connection and try again.');
            setOrders([]);
            setFilteredOrders([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    useEffect(() => {
        filterOrders();
    }, [searchTerm, statusFilter, dateFilter, storeFilter, orders]);

    const filterOrders = () => {
        let filtered = [...orders];

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(order => {
                if (!order) return false;
                return (
                    (order.id && order.id.toLowerCase().includes(term)) ||
                    (order.store_id && order.store_id.toLowerCase().includes(term)) ||
                    (order.order_number && order.order_number.toLowerCase().includes(term)) ||
                    (order.user_id && order.user_id.toLowerCase().includes(term))
                );
            });
        }

        if (statusFilter) {
            filtered = filtered.filter(order => order.status === statusFilter);
        }

        if (dateFilter) {
            const filterDate = new Date(dateFilter);
            filtered = filtered.filter(order => {
                if (!order.placed_at) return false;
                const orderDate = new Date(order.placed_at);
                return orderDate.toDateString() === filterDate.toDateString();
            });
        }

        if (storeFilter) {
            filtered = filtered.filter(order => order.store_id === storeFilter);
        }

        setFilteredOrders(filtered);
        calculateStats(filtered);
    };

    const calculateStats = (ordersList) => {
        const newStats = {
            total: ordersList.length,
            placed: 0,
            confirmed: 0,
            shipped: 0,
            delivered: 0,
            cancelled: 0,
            revenue: 0
        };

        ordersList.forEach(order => {
            if (order && order.status) {
                newStats[order.status] = (newStats[order.status] || 0) + 1;
                newStats.revenue += parseFloat(order.total || order.total_amount || 0);
            }
        });

        setStats(newStats);
    };

    const handleStatusUpdate = async (e) => {
        e.preventDefault();
        try {
            await ordersAPI.updateStatus(selectedOrder.id, statusUpdate);
            setShowStatusModal(false);
            setStatusUpdate('');
            setSuccess(`Order ${selectedOrder.order_number || selectedOrder.id} status updated to ${statusUpdate}`);
            setTimeout(() => setSuccess(''), 3000);
            setSelectedOrder(null);
            fetchOrders();
        } catch (err) {
            setError('Failed to update order status');
        }
    };

    const handleCancel = async (orderId) => {
        try {
            await ordersAPI.cancel(orderId);
            setSuccess('Order cancelled successfully');
            setTimeout(() => setSuccess(''), 3000);
            fetchOrders();
        } catch (err) {
            setError('Failed to cancel order');
        }
    };

    const handleDelete = async () => {
        try {
            await ordersAPI.delete(selectedOrder.id);
            setShowDeleteModal(false);
            setSuccess(`Order ${selectedOrder.order_number || selectedOrder.id} deleted successfully`);
            setTimeout(() => setSuccess(''), 3000);
            setSelectedOrder(null);
            fetchOrders();
        } catch (err) {
            setError('Failed to delete order');
        }
    };

    const openStatusModal = (order, newStatus = '') => {
        setSelectedOrder(order);
        setStatusUpdate(newStatus || order.status);
        setShowStatusModal(true);
    };

    const openDeleteModal = (order) => {
        setSelectedOrder(order);
        setShowDeleteModal(true);
    };

    const openDetailsModal = (order) => {
        setSelectedOrder(order);
        setShowDetailsModal(true);
    };

    const getStatusVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'placed': return 'primary';
            case 'confirmed': return 'info';
            case 'processing': return 'warning';
            case 'shipped': return 'warning';
            case 'delivered': return 'success';
            case 'cancelled': return 'danger';
            case 'refunded': return 'secondary';
            default: return 'secondary';
        }
    };

    const getStores = () => {
        const stores = [...new Set(orders.map(order => order?.store_id).filter(Boolean))];
        return stores;
    };

    if (loading) return (
        <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary" style={{ width: '3rem', height: '3rem' }}>
                <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading orders...</p>
        </div>
    );

    return (
        <div className="container-fluid px-4">
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="fw-bold mb-1">Orders Management</h1>
                    <p className="text-muted mb-0">View and manage all customer orders</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => setShowCreateModal(true)}
                        className="d-flex align-items-center py-2 px-4"
                    >
                        <FaPlus className="me-2" />
                        Create New Order
                    </Button>
                </Col>
            </Row>

            {/* Alerts */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
                    <strong>Error:</strong> {error}
                </Alert>
            )}
            {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')} className="mb-4">
                    <strong>Success:</strong> {success}
                </Alert>
            )}

            {/* Statistics Cards */}
            <Row className="mb-4 g-3">
                <Col xl={3} lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                                    <FaShoppingCart className="text-primary fs-3" />
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1">Total Orders</h6>
                                    <h2 className="fw-bold mb-0">{stats.total}</h2>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-success bg-opacity-10 rounded-circle p-3 me-3">
                                    <FaReceipt className="text-success fs-3" />
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1">Total Revenue</h6>
                                    <h2 className="fw-bold mb-0">{formatCurrency(stats.revenue)}</h2>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                                    <FaBox className="text-warning fs-3" />
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1">Active Orders</h6>
                                    <h2 className="fw-bold mb-0">{stats.placed + stats.confirmed}</h2>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
                <Col xl={3} lg={6}>
                    <Card className="border-0 shadow-sm h-100">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center">
                                <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                                    <FaTruck className="text-info fs-3" />
                                </div>
                                <div>
                                    <h6 className="text-muted mb-1">Delivered</h6>
                                    <h2 className="fw-bold mb-0">{stats.delivered}</h2>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Filters Card */}
            <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-4">
                    <Row className="g-3 align-items-center">
                        <Col xl={3} lg={4} md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search orders..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5 py-2"
                                    size="lg"
                                />
                            </div>
                        </Col>
                        <Col xl={2} lg={3} md={6}>
                            <Form.Select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="py-2"
                                size="lg"
                            >
                                <option value="">All Statuses</option>
                                <option value="placed">Placed</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                            </Form.Select>
                        </Col>
                        <Col xl={2} lg={3} md={6}>
                            <Form.Control
                                type="date"
                                value={dateFilter}
                                onChange={(e) => setDateFilter(e.target.value)}
                                className="py-2"
                                size="lg"
                            />
                        </Col>
                        <Col xl={2} lg={2} md={6}>
                            <Form.Select
                                value={storeFilter}
                                onChange={(e) => setStoreFilter(e.target.value)}
                                className="py-2"
                                size="lg"
                            >
                                <option value="">All Stores</option>
                                {getStores().map(store => (
                                    <option key={store} value={store}>{store}</option>
                                ))}
                            </Form.Select>
                        </Col>
                        <Col xl={3} lg={12} className="d-flex align-items-center justify-content-lg-end">
                            <Button
                                variant="outline-secondary"
                                onClick={() => {
                                    setSearchTerm('');
                                    setStatusFilter('');
                                    setDateFilter('');
                                    setStoreFilter('');
                                }}
                                className="me-3 py-2"
                                size="lg"
                            >
                                Clear Filters
                            </Button>
                            <div className="text-end">
                                <div className="text-muted small">Showing {filteredOrders.length} of {orders.length} orders</div>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Orders Table */}
            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="table-responsive">
                        <Table hover className="mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="py-3 ps-4">Order #</th>
                                    <th className="py-3">Customer</th>
                                    <th className="py-3">Store</th>
                                    <th className="py-3">Amount</th>
                                    <th className="py-3">Status</th>
                                    <th className="py-3">Date</th>
                                    <th className="py-3 pe-4 text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrders.length > 0 ? (
                                    filteredOrders.map(order => {
                                        if (!order) return null;
                                        return (
                                            <tr key={order.id} className="border-top">
                                                <td className="ps-4">
                                                    <div className="fw-bold">{order.order_number || order.id?.substring(0, 12) || 'N/A'}</div>
                                                    <small className="text-muted">{order.id?.substring(0, 8)}...</small>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <FaUser className="text-muted me-2" />
                                                        <span>{order.user_id?.substring(0, 8) || 'Guest'}...</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <FaStore className="text-muted me-2" />
                                                        <span>{order.store_id?.substring(0, 8) || 'N/A'}...</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="fw-bold">{formatCurrency(order.total || order.total_amount)}</div>
                                                    <small className="text-muted">
                                                        Items: {formatCurrency(order.subtotal || order.subtotal_amount)}
                                                    </small>
                                                </td>
                                                <td>
                                                    <Badge
                                                        bg={getStatusVariant(order.status)}
                                                        className="px-3 py-2 text-capitalize"
                                                    >
                                                        {order.status || 'Unknown'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="small">{formatDate(order.placed_at)}</div>
                                                </td>
                                                <td className="pe-4 text-end">
                                                    <Dropdown align="end">
                                                        <Dropdown.Toggle
                                                            variant="outline-secondary"
                                                            id={`dropdown-${order.id}`}
                                                            className="px-3"
                                                        >
                                                            <FaEllipsisV />
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu>
                                                            <Dropdown.Item onClick={() => openDetailsModal(order)}>
                                                                <FaEye className="me-2" />
                                                                View Details
                                                            </Dropdown.Item>
                                                            <Dropdown.Item onClick={() => openStatusModal(order)}>
                                                                <FaEdit className="me-2" />
                                                                Update Status
                                                            </Dropdown.Item>
                                                            <Dropdown.Item
                                                                onClick={() => handleCancel(order.id)}
                                                                disabled={order.status === 'cancelled' || order.status === 'delivered'}
                                                                className={order.status === 'cancelled' || order.status === 'delivered' ? 'text-muted' : ''}
                                                            >
                                                                Cancel Order
                                                            </Dropdown.Item>
                                                            <Dropdown.Divider />
                                                            <Dropdown.Item
                                                                onClick={() => openDeleteModal(order)}
                                                                className="text-danger"
                                                            >
                                                                <FaTrash className="me-2" />
                                                                Delete Order
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">
                                            <div className="text-muted py-5">
                                                <FaBox className="fs-1 mb-3 opacity-25" />
                                                <h5>No orders found</h5>
                                                <p className="mb-0">
                                                    {searchTerm || statusFilter || dateFilter || storeFilter
                                                        ? 'Try adjusting your filters'
                                                        : 'No orders have been placed yet'}
                                                </p>
                                                {!searchTerm && !statusFilter && !dateFilter && !storeFilter && (
                                                    <Button
                                                        variant="outline-primary"
                                                        className="mt-3"
                                                        onClick={() => setShowCreateModal(true)}
                                                    >
                                                        <FaPlus className="me-2" />
                                                        Create First Order
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Card.Body>
            </Card>

            {/* Status Update Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Update Order Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleStatusUpdate}>
                        <Form.Group className="mb-4">
                            <Form.Label>Order</Form.Label>
                            <Form.Control
                                type="text"
                                value={selectedOrder?.order_number || selectedOrder?.id || ''}
                                disabled
                                readOnly
                            />
                        </Form.Group>
                        <Form.Group className="mb-4">
                            <Form.Label>New Status</Form.Label>
                            <Form.Select
                                value={statusUpdate}
                                onChange={(e) => setStatusUpdate(e.target.value)}
                                required
                                size="lg"
                            >
                                <option value="placed">Placed</option>
                                <option value="confirmed">Confirmed</option>
                                <option value="processing">Processing</option>
                                <option value="shipped">Shipped</option>
                                <option value="delivered">Delivered</option>
                                <option value="cancelled">Cancelled</option>
                            </Form.Select>
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="outline-secondary" onClick={() => setShowStatusModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Update Status
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Order</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="lead">
                        Are you sure you want to delete order <strong>{selectedOrder?.order_number || selectedOrder?.id || ''}</strong>?
                    </p>
                    <p className="text-danger">
                        This action cannot be undone. All order data will be permanently removed.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete Order
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Create Order Modal */}
            {showCreateModal && (
                <OrderCreateModal
                    show={showCreateModal}
                    onHide={() => setShowCreateModal(false)}
                    onSuccess={() => {
                        setSuccess('Order created successfully');
                        setTimeout(() => setSuccess(''), 3000);
                        fetchOrders();
                    }}
                />
            )}

            {/* Order Details Modal */}
            {showDetailsModal && selectedOrder && (
                <OrderDetailsModal
                    show={showDetailsModal}
                    onHide={() => {
                        setShowDetailsModal(false);
                        setSelectedOrder(null);
                    }}
                    order={selectedOrder}
                />
            )}
        </div>
    );
};

export default Orders;